import { DEFAULT_SIZING } from './sizing.js';
// Veta Change (módulo): flujo profesional de un requerimiento (DECISION_FLUJO_REQ.md).
// El ticket conserva su máquina de estados; el registro de cambio lleva la etapa fina.
export const DISCIPLINES = ['functional', 'abap'];
export const DISCIPLINE_LABEL = { functional: 'Funcional', abap: 'ABAP' };
/** Rubros por omisión (acordados 15-oct-2026); cada empresa los ajusta. */
export const DEFAULT_REQ_PHASES = [
    { id: 'analysis', name: 'Análisis', active: true },
    { id: 'design', name: 'Diseño', active: true },
    { id: 'build', name: 'Desarrollo / Configuración', active: true },
    { id: 'testing', name: 'Pruebas', active: true },
    { id: 'docs', name: 'Documentación', active: true },
    { id: 'closing', name: 'Cierre', active: true }
];
export const DEFAULT_REQ_SETTINGS = {
    phases: DEFAULT_REQ_PHASES,
    roundingStep: 0.5,
    managementPctRange: [10, 15],
    uatAutoAcceptBusinessDays: 5,
    uatReminderBusinessDay: 3,
    commitmentAlertPct: [75, 100],
    evolutiveAutoConfirm: false
};
export function normalizeReqSettings(raw) {
    const d = DEFAULT_REQ_SETTINGS;
    const num = (v, def, min, max) => typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : def;
    const phases = Array.isArray(raw?.phases) && raw.phases.length
        ? raw.phases.filter(p => p && typeof p.id === 'string' && typeof p.name === 'string').map(p => ({ id: p.id, name: p.name, active: p.active !== false }))
        : d.phases.map(p => ({ ...p }));
    const range = raw?.managementPctRange;
    return {
        phases,
        roundingStep: num(raw?.roundingStep, d.roundingStep, 0.25, 8),
        managementPctRange: Array.isArray(range) && range.length === 2 && range[0] <= range[1] ? [range[0], range[1]] : [...d.managementPctRange],
        uatAutoAcceptBusinessDays: num(raw?.uatAutoAcceptBusinessDays, d.uatAutoAcceptBusinessDays, 1, 30),
        uatReminderBusinessDay: num(raw?.uatReminderBusinessDay, d.uatReminderBusinessDay, 1, 29),
        commitmentAlertPct: [...d.commitmentAlertPct],
        evolutiveAutoConfirm: raw?.evolutiveAutoConfirm === true
    };
}
// ---------- etapas ----------
export const CHANGE_STAGES = [
    'estimating', 'manager_review', 'pending_approval', 'approved', 'development',
    'uat', 'correction', 'deployment', 'closed', 'rejected', 'cancelled', 'converted'
];
export const CHANGE_STAGE_LABEL = {
    estimating: 'En estimación',
    manager_review: 'En revisión del gestor',
    pending_approval: 'Pendiente de aprobación',
    approved: 'Aprobado',
    development: 'En desarrollo',
    uat: 'En pruebas del usuario',
    correction: 'En corrección',
    deployment: 'Paso a producción',
    closed: 'Cerrado',
    rejected: 'Rechazado',
    cancelled: 'Cancelado',
    converted: 'Convertido a proyecto'
};
/** Columnas del tablero de cambios (las etapas finales se ven aparte). */
export const CHANGE_BOARD = ['estimating', 'manager_review', 'pending_approval', 'approved', 'development', 'uat', 'correction', 'deployment'];
export const EMPTY_ESTIMATE = {
    lines: [], requiresAbap: false, abapEstimatedBy: null, scope: '', acceptanceCriteria: [],
    outOfScope: '', assumptions: '', commitmentBusinessDays: 0
};
export const BILLING_CLASS_LABEL = {
    contract: 'Consume contrato',
    additional: 'Facturación adicional',
    project: 'Proyecto'
};
const round2 = (n) => Math.round(n * 100) / 100;
export function estimateTotals(e) {
    const lines = e.lines.filter(l => l.hours > 0 && (e.requiresAbap || l.discipline !== 'abap'));
    const sum = (d) => round2(lines.filter(l => !d || l.discipline === d).reduce((a, l) => a + l.hours, 0));
    return { functional: sum('functional'), abap: sum('abap'), total: sum() };
}
/** Clasificación según horas (la preliminar usa el total del consultor; la definitiva, el subtotal con riesgo). */
export function billingClassFor(hours, sizing = DEFAULT_SIZING) {
    if (hours > sizing.reqMaxHours)
        return 'project';
    return hours > sizing.bagMaxHours ? 'additional' : 'contract';
}
/** Lo que falta para poder enviar la estimación al gestor (vacío = lista). */
export function estimateProblems(e) {
    const t = estimateTotals(e);
    const problems = [];
    if (t.total <= 0)
        problems.push('Captura las horas por rubro');
    if (e.requiresAbap && t.abap <= 0)
        problems.push('Marcaste que requiere ABAP: captura las horas de ABAP');
    if (e.lines.some(l => !(l.hours >= 0) || l.hours > 10_000))
        problems.push('Hay horas inválidas');
    if (!e.scope.trim())
        problems.push('Describe el alcance');
    if (!e.acceptanceCriteria.some(c => c.trim()))
        problems.push('Agrega al menos un criterio de aceptación');
    if (!(e.commitmentBusinessDays > 0))
        problems.push('Indica los días hábiles de entrega');
    return problems;
}
const ceilTo = (n, step) => Math.ceil(n / step - 1e-9) * step;
/**
 * DECISION_FLUJO_REQ §4: el riesgo se reparte en cada celda (rubro × disciplina) y se redondea
 * hacia arriba al paso configurado; la clasificación se mide sobre el subtotal (sin gestión);
 * la gestión se calcula sobre el subtotal; el margen solo afecta el precio.
 */
export function calcChangeQuote(estimate, phases, f, rate, opts = {}) {
    const step = opts.roundingStep ?? 0.5;
    const order = new Map(phases.map((p, i) => [p.id, i]));
    const name = new Map(phases.map(p => [p.id, p.name]));
    const lines = estimate.lines
        .filter(l => l.hours > 0 && (estimate.requiresAbap || l.discipline !== 'abap'))
        .map(l => ({ phase: l.phase, phaseName: name.get(l.phase) ?? l.phase, discipline: l.discipline, hours: ceilTo(l.hours * (1 + f.riskPct / 100), step) }))
        .sort((a, b) => (order.get(a.phase) ?? 99) - (order.get(b.phase) ?? 99) || a.discipline.localeCompare(b.discipline) * -1);
    const subtotalHours = round2(lines.reduce((a, l) => a + l.hours, 0));
    const billingClass = billingClassFor(subtotalHours, opts.sizing ?? DEFAULT_SIZING);
    const managementHours = ceilTo(subtotalHours * f.managementPct / 100, step);
    const totalHours = round2(subtotalHours + managementHours);
    const range = opts.managementPctRange ?? [10, 15];
    const unitPrice = billingClass === 'additional' && rate !== null ? round2(rate * (1 + f.marginPct / 100)) : null;
    return {
        lines, subtotalHours, billingClass, managementHours, totalHours, unitPrice,
        price: unitPrice !== null ? round2(totalHours * unitPrice) : null,
        managementOutOfRange: f.managementPct < range[0] || f.managementPct > range[1]
    };
}
//# sourceMappingURL=change.js.map